import React, { useState, useEffect, useMemo } from "react";
import { IPersonalData } from "../../Common/types";
import { CheckingStatuses, CheckingStatusesDescription, CompanyTypes, CompanyTypesName } from "../../enums/Account";
import { Field } from "../../Types/Form";
import { element } from "prop-types";
import useDataStore from "../../Hooks/useDataStore";
import Input from "../../FormUi/Input";
import { useSignal, useSlot } from 'react-signal-slot';
import Popup from "../../Popups/Popup";
import { TableMode } from "../../enums/TableTypes";


type props = {
    data: IPersonalData;
    save: (arg: any) => any;
    saveLegalRequest: (arg: any) => any
    StatusChecking: CheckingStatuses;
    Mode: TableMode;
    draftRequest: () => any;
}

const EditPersonalData = ({ data, save, StatusChecking, saveLegalRequest, Mode, draftRequest }: props) => {

    const [isRequired, setIsRequired] = useState(true);

    const CompanyTypeField = useMemo(() => {
        if (!data) return null;
        return ({
            name: "CompanyTypeId", title: "Тип аккаунта", editable: Mode === TableMode.Edit, element: "select", require: isRequired, initialValue: data?.CompanyTypeId || CompanyTypes.Fiz,
            options: [
                { name: CompanyTypesName[CompanyTypes.Fiz], value: CompanyTypes.Fiz },
                { name: CompanyTypesName[CompanyTypes.IP], value: CompanyTypes.IP },
                { name: CompanyTypesName[CompanyTypes.OOO], value: CompanyTypes.OOO }
            ]
        })
    }, [data, Mode])

    const [companyTypeId, setCompanyTypeId] = useState(null);
    useEffect(() => {
        if (data?.CompanyTypeId) setCompanyTypeId(data.CompanyTypeId || CompanyTypes.Fiz);
    }, [data])

    const FizFields = useMemo<Field[]>(() => {
        if (!data) return [];
        const fields: Field[] = [
            { name: "LastName", title: "Фамилия", editable: Mode === TableMode.Edit, initialValue: data?.LastName || "", require: isRequired },
            { name: "FirstName", title: "Имя", editable: Mode === TableMode.Edit, initialValue: data?.FirstName || "", require: isRequired },
            { name: "MiddleName", title: "Отчество", editable: Mode === TableMode.Edit, initialValue: data?.MiddleName || "", require: isRequired },
            { name: "PassportSeries", title: "Серия, номер документа", editable: Mode === TableMode.Edit, initialValue: data?.PassportSeries || "", require: isRequired },
            { name: "PassportDate", title: "Дата выдачи", editable: Mode === TableMode.Edit, initialValue: data?.PassportDate ? new Date(data.PassportDate) : "", element: "calendar", require: isRequired },
            { name: "BirthDay", title: "Дата рождения", editable: Mode === TableMode.Edit, initialValue: data?.BirthDay ? new Date(data.BirthDay) : "", element: "calendar", require: isRequired },
            { name: "PassportPlace", title: "Кем выдан документ", editable: Mode === TableMode.Edit, initialValue: data?.PassportPlace || "", require: isRequired },
            { name: "RegAddress", title: "Адрес регистрации", editable: Mode === TableMode.Edit, initialValue: data?.RegAddress || "", require: isRequired },
            { name: "FactAddress", title: "Адрес фактического проживания", editable: Mode === TableMode.Edit, initialValue: data?.FactAddress || "", require: isRequired },
        ];

        if (Mode === TableMode.Edit) {
            fields.push({
                name: "agreement",
                title: "Я согласен с регламентом",
                require: isRequired,
                element: "checkbox",
                error: "Подтвердите согласие с регламентом"
            });
        }

        return fields;
    }, [data, Mode])

    const IpFields = useMemo<Field[]>(() => {
        if (!data) return [];
        const fields: Field[] = [
            { name: "CompanyName", title: "Наименование ИП", editable: Mode === TableMode.Edit, initialValue: data?.CompanyName || "", require: isRequired },
            { name: "INN", title: "ИНН", editable: Mode === TableMode.Edit, initialValue: data?.INN || "", require: isRequired },
            { name: "OGRNIP", title: "ОГРН ИП", editable: Mode === TableMode.Edit, initialValue: data?.OGRNIP || "", require: isRequired },
            { name: "PassportSeries", title: "Серия, номер документа", editable: Mode === TableMode.Edit, initialValue: data?.PassportSeries || "", require: isRequired },
            { name: "PassportDate", title: "Дата выдачи", editable: Mode === TableMode.Edit, initialValue: data?.PassportDate ? new Date(data.PassportDate) : "", element: "calendar", require: isRequired },
            { name: "PassportPlace", title: "Кем выдан документ", editable: Mode === TableMode.Edit, initialValue: data?.PassportPlace || "", require: isRequired },
            { name: "RegAddress", title: "Адрес регистрации", editable: Mode === TableMode.Edit, initialValue: data?.RegAddress || "", require: isRequired },
            { name: "FactAddress", title: "Адрес фактического проживания", editable: Mode === TableMode.Edit, initialValue: data?.FactAddress || "", require: isRequired },
        ];

        if (Mode === TableMode.Edit) {
            fields.push({
                name: "agreement",
                title: "Я согласен с регламентом",
                require: isRequired,
                element: "checkbox",
                error: "Подтвердите согласие с регламентом"
            });
        }

        return fields;
    }, [data, Mode]);

    const OOOFields = useMemo<Field[]>(() => {
        if (!data) return [];
        const fields: Field[] = [
            { name: "CompanyName", title: "Наименование", editable: Mode === TableMode.Edit, initialValue: data?.CompanyName || "", require: isRequired },
            { name: "INN", title: "ИНН", editable: Mode === TableMode.Edit, initialValue: data?.INN || "", require: isRequired },
            { name: "OGRN", title: "ОГРН", editable: Mode === TableMode.Edit, initialValue: data?.OGRN || "", require: isRequired },
            { name: "AdditionalInfo", title: "Действует на основании", editable: Mode === TableMode.Edit, initialValue: data?.AdditionalInfo || "", require: isRequired },
            { name: "RegAddress", title: "Адрес регистрации", editable: Mode === TableMode.Edit, initialValue: data?.RegAddress || "", require: isRequired },
        ];

        if (Mode === TableMode.Edit) {
            fields.push({
                name: "agreement",
                title: "Я согласен с регламентом",
                require: isRequired,
                element: "checkbox",
                error: "Подтвердите согласие с регламентом"
            });
        }

        return fields;
    }, [data, Mode]);



    const activeFields = useMemo(() => {
        if (!data) return [];
        if (Number(companyTypeId) === CompanyTypes.Fiz) return FizFields;
        if (Number(companyTypeId) === CompanyTypes.IP) return IpFields;
        if (Number(companyTypeId) === CompanyTypes.OOO) return OOOFields;
        return FizFields
    }, [data, companyTypeId])

    const { errors, handleChange, values, handleCancel, getData } = useDataStore({ fields: activeFields });


    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupHead, setPopupHead] = useState(null);
    const [popupButtons, setPopupButtons] = useState(null)

    const sendEditRequest = useSignal();
    return (
        <>
            {companyTypeId &&
                <Input
                    field={CompanyTypeField}
                    value={companyTypeId}
                    onChange={(name, value) => setCompanyTypeId(value)}
                    onCancel={() => { setCompanyTypeId(data.CompanyTypeId) }}
                    disabled={Mode === TableMode.View}
                />
            }

            {data && activeFields.map(field =>
                <Input
                    field={field}
                    error={errors[field.name]}
                    value={values?.[field.name]}
                    onChange={handleChange}
                    onCancel={handleCancel}
                    disabled={Mode === TableMode.View}
                />
            )}

            <div className="personalDataButtons">
                {(StatusChecking === CheckingStatuses.Draft || StatusChecking === CheckingStatuses.NoStatus || StatusChecking === CheckingStatuses.CheckFailed) &&
                    <>
                        <button onClick={() => {
                            save({ ...values, CompanyTypeId: String(companyTypeId) });
                        }}>Сохранить</button>
                        <button onClick={() => {
                            getData((data) => {
                                save({ ...data, CompanyTypeId: String(companyTypeId) });
                                saveLegalRequest(data)
                            })
                        }}>Подтвердить Юр.действия</button>
                    </>
                }

                {StatusChecking === CheckingStatuses.Adopted &&
                    <button
                        onClick={() => {
                            setIsPopupOpen(true);
                            setPopupHead({ title: "Внимание", description: "Вам придется пройти проверку заново. Подтвердите действие" })
                            setPopupButtons([
                                { name: "Да", action: () => { draftRequest(); setIsPopupOpen(false) } },
                                { name: "Нет", action: () => setIsPopupOpen(false) }
                            ])
                        }}
                    >Изменить</button>
                }
            </div>

            <Popup
                isOpen={isPopupOpen}
                close={() => setIsPopupOpen(false)}
                head={popupHead}
                actionButtons={popupButtons}
            />
        </>
    )

}


export default EditPersonalData;